import { DischargePatient } from '~/types/discharge';

// Demo: deterministic post-discharge status the receiving agency would share back
// with the referring hospital, framed as the output of an automated voice agent.
// `revision` advances the story each time an update is requested — the first call
// lands before the initial home visit, later calls report on the patient's
// progress.

export interface AgencyFollowUpContent {
  seen: boolean;
  summary: string;
}

export function buildAgencyFollowUp(patient: DischargePatient, revision: number): AgencyFollowUpContent {
  const agency = patient.selectedProviderName ?? 'The receiving agency';

  const steps: AgencyFollowUpContent[] = [
    {
      seen: false,
      summary: `${agency} has accepted the referral and confirmed receipt of the discharge records. The initial home visit is scheduled within 48 hours; the patient has not been seen yet. No barriers to starting care were reported.`,
    },
    {
      seen: true,
      summary: `${agency} completed the first home visit. The patient is settling in at home — alert, ambulating with assistance, and tolerating the transition well. Vitals were stable and medications reconciled, with no acute concerns. Skilled nursing is scheduled 3×/week.`,
    },
    {
      seen: true,
      summary: `${agency} reports steady progress. The patient is more independent with daily activities and pain is well controlled, with no ED visits or readmissions since discharge. The current plan of care continues unchanged.`,
    },
    {
      seen: true,
      summary: `${agency} reports the patient continues to meet recovery goals and the caregiver is comfortable with the plan of care. No new concerns; the agency anticipates a routine discharge from home health services.`,
    },
  ];

  return steps[Math.min(revision, steps.length - 1)];
}
